import React, {useState, useEffect, useContext, ReactNode} from 'react';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import {observer} from 'mobx-react';
import {ModalContext} from '../../../context-providers/ModalProvider';
import {getErrorMessage, hardcodedErrorCodes} from '../../../helpers/error-message.helper';
import {formatCurrencyAmount, getPricePrecision} from '../../../helpers/formatters';
import {
    calculateTakeProfitParamsWithPosition
} from '../../../helpers/math/calculate-take-profit-params';
import "./EditPosition.scss";
import {
    calculateStopLossLimitWithVolatility,
    calculateTakeProfitLimitWithVolatility
} from '../../../helpers/math/take-profit-stop-loss.limit-helper';
import {textToTextNumberModifier, textToTextNumberModifierWithNegative} from '../../../helpers/text-to-number-modifier';
import {IMinMaxValue} from '../../OpenPosition/OpenPosition';

import LabelValue from '../../LabelValue/LabelValue';
import NereusButton from '../../NereusButton/NereusButton';
import ModalWrapper from '../ModalWrapper/ModalWrapper';
import {useSwitchChainToBase} from "../../../hooks/useSwitchChainToBase";

const sanitizeNumbers = (...input: Array<string | undefined>) => {
    return input.map(str => {
        if (typeof str === 'undefined') {
            return str;
        }

        str = textToTextNumberModifier(str);

        if (str === '-') {
            return '0';
        }

        return str;
    });
}

const sanitizeNumbersWithNegative = (...input: Array<string | undefined>) => {
    return input.map(str => {
        if (typeof str === 'undefined') {
            return str;
        }

        str = textToTextNumberModifierWithNegative(str);

        if (str === '-') {
            return '0';
        }

        return str;
    });
}

export interface TpSlParams {
    tp?: number;
    sl?: number;
}

export interface TpSlPositionParams {
    symbol: string;
    tpPrice?: string;
    slPrice?: string;
    amount: string;
    leverage: number | string;
    initialPrice: string;
    lastPrice?: string;
}

interface EditPositionProps {
    title?: ReactNode;
    position: TpSlPositionParams;
    applyChanges?: (tpSl: TpSlParams) => any;
    isEdit?: boolean;
}

interface IMinMaxValuesCollection {
    tp: IMinMaxValue;
    sl: IMinMaxValue;
}

const getDescriptionJsx = (price: string | number) => {
    return <>When the Current Price reaches {formatCurrencyAmount(price, '$')}, a market order will be triggered, and
        the position will be closed</>;
}

const EditPosition: React.FC<EditPositionProps> = observer(({isEdit, title, position, applyChanges}) => {
    const {closeModal} = useContext(ModalContext);
    const switchChainToBase = useSwitchChainToBase();

    // Local state for the input fields
    const [takeProfitPrice, setTakeProfitPrice] = useState(position.tpPrice || '');
    const [takeProfitGain, setTakeProfitGain] = useState('');

    const [stopLossPrice, setStopLossPrice] = useState(position.slPrice || '');
    const [stopLossLoss, setStopLossLoss] = useState('');
    const [isLong, setIsLong] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [validationError, setValidationError] = useState<string | null>(null);
    const [stopLossLimit, setStopLossLimit] = useState<null | number>(null);
    const [takeProfitLimit, setTakeProfitLimit] = useState<null | number>(null);
    const [btnDisabled, setBtnDisabled] = useState<boolean>(true);

    const [minMaxValues, setMinMaxValues] = useState<IMinMaxValuesCollection>({
        tp: {
            min: 0,
            max: Infinity
        },
        sl: {
            min: 0,
            max: Infinity
        }
    });

    useEffect(() => {

        if ((!takeProfitPrice && !stopLossPrice) || validationError) {
            setBtnDisabled(true);
        } else {
            setBtnDisabled(false);
        }

    }, [validationError, stopLossPrice, takeProfitPrice]);

    useEffect(() => {

        const takeProfitNum = takeProfitPrice && Number(takeProfitPrice);
        const stopLossNum = stopLossPrice && Number(stopLossPrice);

        if (takeProfitNum || takeProfitNum === 0) {
            if (takeProfitNum < minMaxValues.tp.min || takeProfitNum > minMaxValues.tp.max) {
                setValidationError(hardcodedErrorCodes.FE_TP_RANGE
                    .replace(/{min}/, formatCurrencyAmount(minMaxValues.tp?.min, '$'))
                    .replace(/{max}/, formatCurrencyAmount(minMaxValues.tp?.max, '$'))
                );
                return;
            }
        }
        if (stopLossNum || stopLossNum === 0) {
            if (stopLossNum < minMaxValues.sl.min || stopLossNum > minMaxValues.sl.max) {
                setValidationError(hardcodedErrorCodes.FE_SL_RANGE
                    .replace(/{min}/, formatCurrencyAmount(minMaxValues.sl?.min, '$', 8))
                    .replace(/{max}/, formatCurrencyAmount(minMaxValues.sl?.max, '$', 8))
                );
                return;
            }
        }
        setValidationError(null);
    }, [minMaxValues, takeProfitPrice, stopLossPrice]);


    useEffect(() => {

        let tpMin = 0;
        let tpMax = Infinity;
        let slMin = 0;
        let slMax = Infinity;

        if (isLong) {
            tpMin = Number(position.initialPrice);
            tpMax = takeProfitLimit || Infinity;
            slMax = Number(position.initialPrice);
            slMin = stopLossLimit || 0;
        } else {
            tpMin = takeProfitLimit || 0;
            tpMax = Number(position.initialPrice);
            slMin = Number(position.initialPrice);
            slMax = stopLossLimit || Infinity;
        }

        setMinMaxValues({
            tp: {
                min: tpMin,
                max: tpMax
            },
            sl: {
                min: slMin,
                max: slMax
            }
        });

    }, [stopLossLimit, takeProfitLimit, isLong, position.initialPrice]);

    useEffect(() => {
        const stopLossLimitCalculated = calculateStopLossLimitWithVolatility(!isLong, position.leverage || 1, position.initialPrice || 0).toNumber();
        let newStopLossLimit = stopLossLimitCalculated;
        if (isEdit) {
            let stopLossFromPosition = Number(position.slPrice);
            newStopLossLimit = Math[isLong ? 'min' : 'max'](stopLossLimitCalculated, stopLossFromPosition);
        }
        setStopLossLimit(newStopLossLimit);
    }, [isLong, position.leverage, position.initialPrice, isEdit]);

    useEffect(() => {
        const [baseAsset] = position.symbol.split(':');
        const takeProfitLimitCalculated = calculateTakeProfitLimitWithVolatility(!isLong, position.leverage || 1, position.initialPrice || 0, baseAsset).toNumber();
        let newTakeProfitLimit = takeProfitLimitCalculated;

        if (isEdit) {
            let takeProfitFromPosition = Number(position.tpPrice);
            newTakeProfitLimit = Math[isLong ? 'max' : 'min'](takeProfitLimitCalculated, takeProfitFromPosition);
        }

        setTakeProfitLimit(newTakeProfitLimit);
    }, [isLong, position.leverage, position.initialPrice, position.symbol, isEdit]);


    useEffect(() => {
        const isLong = position.symbol.split(':')[2] === 'long';
        setIsLong(isLong);
    }, [position.symbol]);

    useEffect(() => {
        calculateTakeProfit(position.tpPrice || '', undefined, undefined);
    }, [position.tpPrice]);

    useEffect(() => {
        calculateStopLoss(position.slPrice || '', undefined, undefined);
    }, [position.slPrice]);

    const onApplyChanges = async () => {
        if (!applyChanges) {
            return;
        }
        try {
            await switchChainToBase()
            await applyChanges({
                tp: takeProfitPrice ? Number(takeProfitPrice) : undefined,
                sl: stopLossPrice ? Number(stopLossPrice) : undefined
            });
            closeModal();
        } catch (e) {
            console.error(e)
            setError(getErrorMessage(e));
        }
    }

    const calculateTakeProfit = (closePrice?: string, closeGain?: string, closeAmount?: string) => {
        let [closePriceSanitized] = sanitizeNumbers(closePrice);
        let [closeGainSanitized, closeAmountSanitized] = sanitizeNumbersWithNegative(closeGain, closeAmount);

        const newTakeProfitParams = calculateTakeProfitParamsWithPosition(
            position,
            true,
            closePriceSanitized,
            closeGainSanitized,
            closeAmountSanitized
        );

        const closePriceValue = closePrice || newTakeProfitParams.closePrice;
        const newClosePrice = textToTextNumberModifier(closePriceValue,  getPricePrecision(closePriceValue));
        setTakeProfitPrice(newClosePrice);
        setTakeProfitGain((closeGain && textToTextNumberModifierWithNegative(closeGain, 2)) || textToTextNumberModifierWithNegative(newTakeProfitParams.closeGainPercent, 2));
    }

    const calculateStopLoss = (closePrice?: string, closeLoss?: string, closeAmount?: string) => {
        let [closePriceSanitized] = sanitizeNumbers(closePrice);
        let [closeLossSanitized, closeAmountSanitized] = sanitizeNumbersWithNegative(closeLoss, closeAmount);

        const newTakeProfitParams = calculateTakeProfitParamsWithPosition(
            position,
            false,
            closePriceSanitized,
            closeLossSanitized,
            closeAmountSanitized
        );

        const closePriceValue = closePrice || newTakeProfitParams.closePrice;
        const newClosePrice = textToTextNumberModifier(closePriceValue,  getPricePrecision(closePriceValue));
        setStopLossPrice(newClosePrice);
        setStopLossLoss((closeLoss && textToTextNumberModifierWithNegative(closeLoss, 2)) || textToTextNumberModifierWithNegative(newTakeProfitParams.closeGainPercent, 2));
    }

    return (
        <ModalWrapper className="EditPosition"
                      headerChildren={title}
                      bodyChildren={<>

                          {isEdit && Number(position.initialPrice) ? <LabelValue label="Open Price"
                                                                                 value={formatCurrencyAmount(position.initialPrice, 'USDC', 8)}/> : null}
                          {isEdit && Number(position.lastPrice) ? <LabelValue label="Current Price:"
                                                                              value={formatCurrencyAmount(position.lastPrice, 'USDC', 8)}/> : null}

                          {isEdit ? <div className="nereus-delimiter"></div> : null}

                          <label className="mb-2 nereus-caption nereus-color-neutral-grey">Take Profit</label>
                          <div className="grid gap-1.5 grid-cols-2 mb-4 mt-1.5">
                              <TextField className="EditPosition__input" label="Price" placeholder="Amount"
                                         value={takeProfitPrice}
                                         InputProps={{
                                             startAdornment: <InputAdornment position="start"><span
                                                 className="nereus-color-neutral-grey">{isLong ? '≥' : '≤'}</span><span
                                                 className="nereus-color-white">$</span></InputAdornment>
                                         }}
                                         onChange={(e) => calculateTakeProfit(e.target.value)} variant="outlined"/>
                              <TextField className="EditPosition__input" placeholder="Gain" label="Price Change"
                                         value={takeProfitGain}
                                         InputProps={{
                                             startAdornment: <InputAdornment position="start"><span
                                                 className="nereus-color-white">%</span></InputAdornment>
                                         }}
                                         onChange={(e) => calculateTakeProfit(undefined, e.target.value)}
                                         variant="outlined"/>
                          </div>

                          <p className="nereus-caption nereus-color-neutral-grey">{getDescriptionJsx(takeProfitPrice)}</p>

                          <div className="nereus-delimiter"></div>

                          <label className="nereus-caption nereus-color-neutral-grey">Stop Loss</label>
                          <div className="grid gap-1.5 grid-cols-2 mb-4 mt-1.5">
                              <TextField className="EditPosition__input" placeholder="Amount" label="Price"
                                         value={stopLossPrice}
                                         InputProps={{
                                             startAdornment: <InputAdornment position="start"><span
                                                 className="nereus-color-neutral-grey">{isLong ? '≤' : '≥'}</span><span
                                                 className="nereus-color-white">$</span></InputAdornment>
                                         }}
                                         onChange={(e) => calculateStopLoss(e.target.value)} variant="outlined"/>
                              <TextField placeholder="Loss" className="EditPosition__input" label="Price Change"
                                         value={stopLossLoss} InputProps={{
                                              startAdornment: <InputAdornment position="start"><span
                                                  className="nereus-color-white">%</span></InputAdornment>
                                          }}
                                         onChange={(e) => calculateStopLoss(undefined, e.target.value)}
                                         variant="outlined"/>
                          </div>
                          <p className="nereus-caption nereus-color-neutral-grey mb-4">{getDescriptionJsx(stopLossPrice)}</p>

                          {(validationError || error) ?
                              <Alert className="DepositDialog__error mt-4 mb-4" variant="standard" severity="error">
                                  <span className="nereus-caption">{error || validationError}</span>
                              </Alert> : null}
                          <NereusButton disabled={btnDisabled} className="w-full mt-4" variant="contained"
                                        onClick={onApplyChanges}>Apply
                              TP/SL</NereusButton>
                      </>}
        />);

});

export default EditPosition;
